import { useCallback, useState } from 'react';
import {
	openPopup,
	pollPopup,
	generateState,
	buildAuthUrl,
} from './OAuthPopup';

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

/**
 * LoginSocialGoogle Component
 *
 * Custom Google OAuth component (Authorization Code flow)
 * Returns authorization CODE (not access token) for secure backend exchange
 *
 * @param {Object} props
 * @param {string} props.client_id - Google OAuth Client ID
 * @param {string} props.redirect_uri - Redirect URI (must match Google Console)
 * @param {string} props.scope - OAuth scopes (default: 'openid profile email')
 * @param {Function} props.onResolve - Success callback ({ provider, data })
 * @param {Function} props.onReject - Error callback
 * @param {boolean} props.isOnlyGetCode - Always true (we only get code, not token)
 * @param {boolean} props.isDisabled - Disable button
 * @param {ReactNode} props.children - Button/trigger element
 */
export const LoginSocialGoogle = ({
	client_id,
	redirect_uri,
	scope = 'openid profile email',
	onResolve,
	onReject,
	isOnlyGetCode = true, // Always get code (not token)
	isDisabled = false,
	children,
}) => {
	const [isLoading, setIsLoading] = useState(false);

	/**
	 * Handle OAuth login click
	 */
	const handleLogin = useCallback(() => {
		if (isDisabled || isLoading) return;

		setIsLoading(true);

		// Generate CSRF state
		const state = generateState();
		localStorage.setItem('oauth_google_state', state);

		// Build authorization URL
		const authUrl = buildAuthUrl(GOOGLE_AUTH_URL, {
			client_id,
			redirect_uri,
			response_type: 'code', // Authorization code flow
			scope,
			state,
			access_type: 'offline', // Get refresh token
			prompt: 'select_account', // Always show account selector
		});

		// Open popup
		const popup = openPopup(authUrl, 'Google Login');

		if (!popup) {
			setIsLoading(false);
			onReject && onReject(new Error('Popup blocked. Please allow popups.'));
			return;
		}

		// Poll popup for callback
		pollPopup(
			popup,
			redirect_uri,
			// Success callback
			({ code, state: returnedState }) => {
				setIsLoading(false);

				// Verify state (CSRF protection)
				const savedState = localStorage.getItem('oauth_google_state');
				localStorage.removeItem('oauth_google_state');

				if (savedState !== returnedState) {
					onReject &&
						onReject(new Error('State mismatch - possible CSRF attack'));
					return;
				}

				// Return code to parent
				onResolve &&
					onResolve({
						provider: 'google',
						data: {
							code,
							redirect_uri, // Backend needs this to exchange code
						},
					});
			},
			// Error callback
			(error) => {
				setIsLoading(false);
				localStorage.removeItem('oauth_google_state');
				onReject && onReject(error);
			},
		);
	}, [
		client_id,
		redirect_uri,
		scope,
		onResolve,
		onReject,
		isDisabled,
		isLoading,
	]);

	// Render children with onClick handler
	return (
		<div
			onClick={handleLogin}
			style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
		>
			{children}
		</div>
	);
};
